/**
 * Deterministic packing-slip pagination.
 *
 * Browser font metrics vary, so pagination uses conservative weighted row units
 * and a rendered overflow preflight. The estimator accounts for wrapping,
 * variations, KG annotations and narrower product columns when prices are shown.
 */
import { CHEETAH_LZ17_PAGINATION } from './cheetahLz17Geometry';

export const PACKING_SLIP_DENSITY_PROFILES = Object.freeze({
  standard: Object.freeze([
    Object.freeze({
      key: 'normal',
      firstCapacity: 24,
      continuationCapacity: 35,
      finalReserve: 5,
      fontPt: 9,
      lineHeightPx: 20,
    }),
    Object.freeze({
      key: 'compact',
      firstCapacity: 30,
      continuationCapacity: 44,
      finalReserve: 4,
      fontPt: 8.5,
      lineHeightPx: 17,
    }),
    Object.freeze({
      key: 'dense',
      firstCapacity: 37,
      continuationCapacity: 54,
      finalReserve: 3.5,
      fontPt: 8,
      lineHeightPx: 15,
    }),
  ]),
  cheetah: CHEETAH_LZ17_PAGINATION,
});

function textLines(text, charsPerLine) {
  const length = String(text || '').trim().length;
  if (!length) return 1;
  return Math.max(1, Math.ceil(length / Math.max(8, charsPerLine)));
}

/**
 * Weighted height cost for one normalized line model.
 * @param {object} line
 * @param {{ format?: 'standard'|'cheetah', showPrices?: boolean }} [options]
 */
export function estimatePackingSlipRowUnits(line, options = {}) {
  const isCheetah = options.format === 'cheetah';
  const charsPerLine = isCheetah
    ? options.showPrices
      ? 34
      : 46
    : options.showPrices
      ? 42
      : 58;

  let units = textLines(line?.name, charsPerLine);
  (line?.variations || []).forEach((variation) => {
    const text = variation?.key
      ? `${variation.key}: ${variation.value || ''}`
      : variation?.value || '';
    units += Math.max(0.7, textLines(text, charsPerLine - 4) * 0.8);
  });
  if (line?.needsKg) units += 0.25;
  // Keep a little space for rows with unusually long unbroken SKU/name tokens.
  if (/\S{30,}/.test(String(line?.name || ''))) units += 0.5;
  return Math.max(1, Math.round(units * 4) / 4);
}

export function getPackingSlipProfiles(format = 'standard') {
  return PACKING_SLIP_DENSITY_PROFILES[format] || PACKING_SLIP_DENSITY_PROFILES.standard;
}

/**
 * Pick the least compact profile that can keep the order on its first page.
 * If continuation is unavoidable, use the readable dense floor to minimise paper.
 */
export function selectPackingSlipDensity(lines, options = {}) {
  const format = options.format || 'standard';
  const firstCapacityPenalty = Math.max(0, Number(options.firstCapacityPenalty) || 0);
  const profiles = getPackingSlipProfiles(format).map((profile) => ({
    ...profile,
    firstCapacity: Math.max(
      1,
      profile.firstCapacity -
        firstCapacityPenalty * (profile.metadataPenaltyScale ?? 1),
    ),
  }));
  const costs = (lines || []).map((line) =>
    estimatePackingSlipRowUnits(line, {
      format,
      showPrices: options.showPrices,
    }),
  );
  const total = costs.reduce((sum, value) => sum + value, 0);
  const fitting = profiles.find(
    (profile) => total + profile.finalReserve <= profile.firstCapacity,
  );
  return fitting || profiles[profiles.length - 1];
}

/**
 * Split normalized lines into explicit physical-page chunks.
 * Every line appears once and remains indivisible.
 */
export function paginatePackingSlipLines(lines, options = {}) {
  const list = Array.isArray(lines) ? lines : [];
  const format = options.format || 'standard';
  const profile =
    options.profile ||
    selectPackingSlipDensity(list, {
      format,
      showPrices: options.showPrices,
      firstCapacityPenalty: options.firstCapacityPenalty,
    });
  const costs = list.map((line) =>
    estimatePackingSlipRowUnits(line, {
      format,
      showPrices: options.showPrices,
    }),
  );

  if (!list.length) {
    return {
      profile,
      chunks: [{ lines: [], units: 0, isFirst: true, isFinal: true }],
    };
  }

  const chunks = [];
  let current = [];
  let currentUnits = 0;
  let pageIndex = 0;

  list.forEach((line, lineIndex) => {
    const cost = costs[lineIndex];
    const capacity =
      pageIndex === 0 ? profile.firstCapacity : profile.continuationCapacity;
    const isLastLine = lineIndex === list.length - 1;
    const required = cost + (isLastLine ? profile.finalReserve : 0);

    if (current.length && currentUnits + required > capacity) {
      chunks.push({
        lines: current,
        units: currentUnits,
        isFirst: pageIndex === 0,
        isFinal: false,
      });
      pageIndex += 1;
      current = [];
      currentUnits = 0;
    }

    current.push(line);
    currentUnits += cost;
  });

  chunks.push({
    lines: current,
    units: currentUnits,
    isFirst: pageIndex === 0,
    isFinal: true,
  });

  const pageCount = chunks.length;
  return {
    profile,
    chunks: chunks.map((chunk, index) => ({
      ...chunk,
      pageIndex: index,
      pageNumber: index + 1,
      pageCount,
      isFirst: index === 0,
      isFinal: index === pageCount - 1,
    })),
  };
}

export default paginatePackingSlipLines;
